import { METALS_ASSEMBLY_QUOTE_DENSITY_LB_PER_CUBIC_INCH } from '../constants/metals-assembly-quote.constant';
import {
    METALS_ASSEMBLY_QUOTE_EMPTY_BREAKDOWN,
    MetalsAssemblyQuoteMaterial,
    MetalsAssemblyQuoteCostBreakdown,
    MetalsAssemblyQuoteCostingContext,
    MetalsAssemblyQuoteLink,
    MetalsAssemblyQuoteNode,
    MetalsAssemblyQuoteOperation,
    MetalsAssemblyQuoteRates,
} from '../models/metals-assembly-quote.model';

/*
 * Metals assembly quote cost math, framework-free so a server function can
 * compute the same number from the same board. Cost is always recipe x rates
 * at a quantity: setup minutes spread across the run is what makes a bigger
 * order cheaper per part, so there is no multiplier table.
 */

export function metalsAssemblyQuoteHourlyRate(rates: MetalsAssemblyQuoteRates, operation: MetalsAssemblyQuoteOperation): number {
    const rate = rates.hourlyRateByTaskType?.[operation.type];
    return typeof rate === 'number' && isFinite(rate) ? rate : 0;
}

export function metalsAssemblyQuotePricePerLb(rates: MetalsAssemblyQuoteRates, family: string | null): number {
    if (!family) {
        return 0;
    }
    const rate = rates.pricePerLbByFamily?.[family];
    return typeof rate === 'number' && isFinite(rate) ? rate : 0;
}

function positive(value: number | null | undefined): number {
    return typeof value === 'number' && isFinite(value) && value > 0 ? value : 0;
}

/* ================================================================= material */

export function metalsAssemblyQuoteDensity(family: string | null): number {
    const density = family ? METALS_ASSEMBLY_QUOTE_DENSITY_LB_PER_CUBIC_INCH[family] : 0;
    return typeof density === 'number' && isFinite(density) ? density : 0;
}

/** cross-section of a tube or bar in square inches; a wall thicker than the section reads as solid */
export function metalsAssemblyQuoteSectionSquareInches(material: MetalsAssemblyQuoteMaterial): number {
    const a = positive(material.sizeIn);
    const b = material.profile === 'rectangular' ? positive(material.widthIn) : a;
    const solid = material.profile === 'round' ? Math.PI / 4 * a * a : a * b;
    if (material.form !== 'tube') {
        return solid;
    }
    const wall = positive(material.thicknessIn);
    const innerA = Math.max(0, a - 2 * wall);
    const innerB = Math.max(0, b - 2 * wall);
    const hollow = material.profile === 'round' ? Math.PI / 4 * innerA * innerA : innerA * innerB;
    return solid - hollow;
}

/** cubic inches of stock in one part, from the form's dimensions */
export function metalsAssemblyQuoteStockCubicInches(material: MetalsAssemblyQuoteMaterial): number {
    switch (material.form) {
        case 'sheet':
            return positive(material.thicknessIn) * positive(material.lengthIn) * positive(material.widthIn);
        case 'tube':
        case 'bar':
            return metalsAssemblyQuoteSectionSquareInches(material) * positive(material.lengthIn);
        default:
            return 0;
    }
}

/** pounds of raw material in one part: typed for the weight form, derived from dimensions and density otherwise */
export function metalsAssemblyQuoteMaterialWeightLb(material: MetalsAssemblyQuoteMaterial | null | undefined): number {
    if (!material || !material.family) {
        return 0;
    }
    if (material.form === 'weight') {
        return positive(material.weightLb);
    }
    return metalsAssemblyQuoteStockCubicInches(material) * metalsAssemblyQuoteDensity(material.family);
}

export function metalsAssemblyQuoteMaterialCost(rates: MetalsAssemblyQuoteRates, material: MetalsAssemblyQuoteMaterial | null | undefined): number {
    if (!material) {
        return 0;
    }
    return metalsAssemblyQuoteMaterialWeightLb(material) * metalsAssemblyQuotePricePerLb(rates, material.family);
}

/** the links that make other nodes components of this one */
export function metalsAssemblyQuoteComponentLinks(links: readonly MetalsAssemblyQuoteLink[], nodeId: string): MetalsAssemblyQuoteLink[] {
    return links.filter(link => link.to === nodeId);
}

/** minutes of one operation per part, setup spread across the run */
export function metalsAssemblyQuoteOperationMinutesPerPart(operation: MetalsAssemblyQuoteOperation, quantity: number): number {
    const run = Math.max(1, positive(quantity));
    return positive(operation.setupMinutes) / run + positive(operation.minutesPerPart);
}

/**
 * Per-part cost of a node when `quantity` of it are made. Children are costed
 * at their own run size — this node's quantity x the link quantity — so their
 * setup is spread over what the parent actually consumes.
 *
 * `path` guards against loops: a node already on the way down costs nothing a
 * second time. A diamond (one child under two parents) is fine, because the
 * guard is the current path and not everything ever visited.
 */
export function calculateMetalsAssemblyQuoteNodeCost(
    context: MetalsAssemblyQuoteCostingContext,
    node: MetalsAssemblyQuoteNode,
    quantity: number,
    path: Set<string> = new Set<string>(),
): MetalsAssemblyQuoteCostBreakdown {
    if (path.has(node.id)) {
        return { ...METALS_ASSEMBLY_QUOTE_EMPTY_BREAKDOWN };
    }
    path.add(node.id);
    const run = Math.max(1, positive(quantity));

    const material = metalsAssemblyQuoteMaterialCost(context.rates, node.material);

    let labor = 0;
    let outside = 0;
    for (const operation of node.operations ?? []) {
        labor += metalsAssemblyQuoteOperationMinutesPerPart(operation, run) * metalsAssemblyQuoteHourlyRate(context.rates, operation) / 60;
        outside += positive(operation.costPerPart);
    }

    const hardware = (node.hardware ?? []).reduce((sum, row) => sum + positive(row.quantity) * positive(row.unitCost), 0);

    let components = 0;
    for (const link of metalsAssemblyQuoteComponentLinks(context.links, node.id)) {
        const child = context.nodes.find(candidate => candidate.id === link.from);
        const perParent = positive(link.quantity);
        if (!child || perParent === 0) {
            continue;
        }
        components += perParent * calculateMetalsAssemblyQuoteNodeCost(context, child, run * perParent, path).total;
    }

    path.delete(node.id);
    const total = material + labor + outside + hardware + components;
    return { material, labor, outside, hardware, components, total };
}

/** true when linking child -> parent would close a loop */
export function metalsAssemblyQuoteWouldCycle(links: readonly MetalsAssemblyQuoteLink[], childId: string, parentId: string): boolean {
    if (childId === parentId) {
        return true;
    }
    // walk down from the child: if the parent is already one of its descendants, the link loops
    const seen = new Set<string>();
    const stack = [childId];
    while (stack.length) {
        const current = stack.pop() as string;
        if (current === parentId) {
            return true;
        }
        if (seen.has(current)) {
            continue;
        }
        seen.add(current);
        for (const link of links) {
            if (link.to === current) {
                stack.push(link.from);
            }
        }
    }
    return false;
}

/** sell price for a cost at a margin on price: cost / (1 - m) */
export function metalsAssemblyQuotePriceFromMargin(cost: number, marginPercent: number): number {
    const margin = Math.min(99, Math.max(0, positive(marginPercent))) / 100;
    return cost / (1 - margin);
}
